import React, { createContext, useState, useEffect, useContext } from 'react';

export const AccountContext = createContext();

export function AccountProvider({ children }) {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isAccountSelected, setIsAccountSelected] = useState(false);

  useEffect(() => {
    // Check if user explicitly requested overview (via localStorage flag)
    const showOverview = localStorage.getItem('showAccountOverview');
    if (showOverview === 'true') {
      // User wants to see overview, don't auto-load account
      console.log('👤 User requested overview - showing account selector');
      return;
    }
    
    const saved = localStorage.getItem('selectedGoogleAdsAccount');
    if (saved) {
      try {
        const account = JSON.parse(saved);
        
        // SECURITY: Verify the saved account is still in linked accounts
        const savedLinked = localStorage.getItem('linkedGoogleAdsAccounts');
        const linkedAccountIds = savedLinked ? JSON.parse(savedLinked) : [];
        
        if (!linkedAccountIds.includes(account.id)) {
          console.warn(`🚫 [SECURITY] Saved account ${account.id} is no longer linked - clearing selection`);
          localStorage.removeItem('selectedGoogleAdsAccount');
          // Don't set as selected - force user back to account selector
          return;
        }
        
        setSelectedAccount(account);
        setIsAccountSelected(true);
      } catch (e) {
        console.error('Failed to parse saved account:', e);
        localStorage.removeItem('selectedGoogleAdsAccount');
      }
    }
  }, []);

  const selectAccount = (account) => {
    // SECURITY: Verify the account is in the linked accounts list
    const savedLinked = localStorage.getItem('linkedGoogleAdsAccounts');
    const linkedAccountIds = savedLinked ? JSON.parse(savedLinked) : [];
    
    if (!linkedAccountIds.includes(account.id)) {
      console.error(`🚫 [SECURITY] Attempted to select unlinked account: ${account.id} (${account.name})`);
      console.error(`   Linked accounts: ${linkedAccountIds.join(', ')}`);
      // Don't allow selection - user must link the account first
      alert(`Account "${account.name}" is not linked. Please link it first in the Account Overview.`);
      return;
    }
    
    console.log(`✅ Account verified as linked: ${account.name}`);
    setSelectedAccount(account);
    setIsAccountSelected(true);
    localStorage.setItem('selectedGoogleAdsAccount', JSON.stringify(account));
    localStorage.removeItem('showAccountOverview'); // Clear overview flag
  };

  const clearAccount = () => {
    console.log('🔄 Clearing account - showing overview');
    // Save last account so user can return to dashboard
    const current = localStorage.getItem('selectedGoogleAdsAccount');
    if (current) {
      localStorage.setItem('lastSelectedGoogleAdsAccount', current);
    }
    setSelectedAccount(null);
    setIsAccountSelected(false);
    localStorage.removeItem('selectedGoogleAdsAccount');
    localStorage.setItem('showAccountOverview', 'true');
  };

  return (
    <AccountContext.Provider value={{ 
      selectedAccount, 
      isAccountSelected,
      selectAccount,
      clearAccount
    }}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  return useContext(AccountContext);
}

